import animateScrollTo from 'animated-scroll-to';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

/**
 * Smooth scroll to an element in the form wizard.
 *
 * @param {String} el The element to scroll to
 * @param {Number} timeout Begin scrolling after element has attached to the DOM
 * @param {Number} verticalOffset The amount of space to show before the element
 * @param {Boolean} useFormMutationObserver Use MutationObserver to watch for when the element is available in the DOM
 *
 */
export function scrollToElement(el = 'body', timeout = 100, verticalOffset = 0, useFormMutationObserver = false) {
  if (useFormMutationObserver) {
    scrollWithFormMutationObserver(el, timeout, verticalOffset);
    return;
  }
  const elementToScrollTo = document.querySelector(el);
  scrollToElementRaw(elementToScrollTo, timeout, verticalOffset);
}

export function scrollToElementRaw(
  el: Element | null | undefined,
  timeout = 100,
  verticalOffset = 0,
  container: Element | Window = window
) {
  if (el) {
    setTimeout(() => {
      animateScrollTo(el, {
        minDuration: 400,
        maxDuration: 600,
        verticalOffset,
        elementToScroll: container,
      });
    }, timeout);
  }
}

export function isDevEnv() {
  return process.env.NODE_ENV === 'development';
}

/**
 * Obtain the screen size using Tailwind breakpoints.
 *
 * @returns {Record<string, boolean>}
 */
export function getScreenSize() {
  try {
    const breakpoints = useBreakpoints(breakpointsTailwind);

    const xs = breakpoints.smaller('sm');
    const xse = breakpoints.smallerOrEqual('sm');
    const sm = breakpoints.between('sm', 'md');
    const md = breakpoints.between('md', 'lg');
    const lg = breakpoints.between('lg', 'xl');
    const xl = breakpoints.between('xl', '2xl');
    const xxl = breakpoints['2xl'];

    return {
      xs,
      xse,
      sm,
      md,
      lg,
      xl,
      xxl,
    };
  } catch {
    return {
      xs: { value: false },
      xse: { value: false },
      sm: { value: false },
      md: { value: false },
      lg: { value: false },
      xl: { value: false },
      xxl: { value: false },
    };
  }
}

export function isMobileDevice() {
  const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
}

export function isMacOS() {
  return (
    navigator?.userAgent?.toLowerCase().includes('mac os') &&
    !navigator?.userAgent?.toLowerCase().includes('like mac os')
  );
}

/**
 * Check if the user is at the bottom
 *
 * @returns {boolean} isReachBottom
 */
export function isReachBottom() {
  return document.body.scrollHeight - window.innerHeight <= window.scrollY;
}

/**
 * Focus the first tabbable element inside a container
 *
 * @param container - Selector string or HTMLElement
 * @param tabbableSelector - Optional selector for tabbable elements
 */
export function focusFirstTabbableWhenReady(
  container: string | HTMLElement,
  options: {
    timeout?: number;
    tabbableSelector?: string;
    retryAfterTimeout?: boolean;
  } = {}
) {
  const {
    timeout = 1000,
    tabbableSelector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    retryAfterTimeout = true,
  } = options;

  let rootEl: HTMLElement | null = null;

  if (typeof container === 'string') {
    rootEl = document.querySelector(container);
  } else if (container instanceof HTMLElement) {
    rootEl = container;
  }

  if (!rootEl) return;

  const tryFocus = () => {
    const firstTabbable = rootEl!.querySelector(tabbableSelector) as HTMLElement | null;
    if (firstTabbable) {
      firstTabbable.focus({ preventScroll: true });
      return true;
    }
    return false;
  };

  if (!tryFocus() && retryAfterTimeout) {
    // Fallback: retry after timeout
    setTimeout(() => {
      tryFocus();
    }, timeout);
  }
}

/**
 * Scroll to an element in the form using MutationObserver to watch for changes.
 * Useful for when the element is not immediately available in the DOM.
 *
 * @param {String} el The element to scroll to
 * @param {Number} timeout Begin scrolling after element has attached to the DOM
 * @param {Number} verticalOffset The amount of space to show before the element
 * @param {Number} mutationObserverTimeout The amount of time to wait before disconnecting the observer
 *
 */
function scrollWithFormMutationObserver(
  el = 'body',
  timeout = 100,
  verticalOffset = 0,
  mutationObserverTimeout = 3000
) {
  // Select the node that will be observed for mutations
  const targetNode = document.getElementById('base-form-container');
  if (targetNode === null) return;

  // Callback function to execute when mutations are observed
  const callback = () => {
    const elementToScrollTo = document.querySelector(el);
    if (elementToScrollTo) {
      scrollToElementRaw(elementToScrollTo, timeout, verticalOffset);
    }
  };

  // Disconnect current observer
  disconnectObserver();
  // Create an observer instance linked to the callback function
  const observer = new MutationObserver(callback);

  if (typeof window != 'undefined') (window as any)['RACQ_JSS_FORMS_SCROLL_OBSERVER'] = observer;

  // Start observing the target node for configured mutations
  observer.observe(targetNode, { childList: true, subtree: true });

  // Disconnect after timeout
  setTimeout(disconnectObserver, mutationObserverTimeout);
}

// Disconnect on user scroll
typeof window != 'undefined' && window.addEventListener('wheel', disconnectObserver);

function disconnectObserver() {
  if (typeof window == 'undefined') return;
  try {
    if ((window as any)['RACQ_JSS_FORMS_SCROLL_OBSERVER']) {
      (window as any)['RACQ_JSS_FORMS_SCROLL_OBSERVER'].disconnect();
      (window as any)['RACQ_JSS_FORMS_SCROLL_OBSERVER'] = undefined;
    }
  } catch (e) {
    console.warn('Unable to disconnect mutation observer: ', e);
  }
}
